import { motion } from 'framer-motion';
import { PartyPopper, ArrowRight, ArrowLeft } from 'lucide-react';
import { springConfig, type ClaimTheme } from '../constants';
import { AnimatedButton } from '../shared/AnimatedButton';
import { ConfettiCanvas } from '../shared/ConfettiCanvas';

interface SuccessCelebrationProps {
  caseNumber: string;
  onViewCase: () => void;
  onBackToOverview: () => void;
  theme?: ClaimTheme;
}

export function SuccessCelebration({
  caseNumber,
  onViewCase,
  onBackToOverview,
  theme = 'invol',
}: SuccessCelebrationProps) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      {/* Confetti */}
      <ConfettiCanvas active={true} />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={springConfig.bouncy}
        className="text-center max-w-md mx-auto px-4"
      >
        {/* Success icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            ...springConfig.celebration,
            delay: 0.2,
          }}
          className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-xl shadow-emerald-500/30"
        >
          <PartyPopper className="w-12 h-12 text-white" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-3xl font-bold text-slate-800 mb-2"
        >
          Anspruch eingereicht!
        </motion.h1>

        {/* Case number */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-4"
        >
          <span className="text-slate-500">Fallnummer: </span>
          <span className="font-mono font-bold text-lg bg-emerald-100 text-emerald-700 px-3 py-1 rounded-lg">
            {caseNumber}
          </span>
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-slate-500 mb-8"
        >
          Ein Anwalt wird Ihren Fall innerhalb von 24 Stunden prüfen.
          <br />
          Sie erhalten E-Mail-Updates zum Fortschritt.
        </motion.p>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <AnimatedButton
            variant="primary"
            size="lg"
            onClick={onViewCase}
            icon={<ArrowRight className="w-5 h-5" />}
            theme={theme}
          >
            Meinen Fall ansehen
          </AnimatedButton>

          <AnimatedButton
            variant="secondary"
            size="lg"
            onClick={onBackToOverview}
            icon={<ArrowLeft className="w-5 h-5" />}
            theme={theme}
          >
            Zur Übersicht
          </AnimatedButton>
        </motion.div>
      </motion.div>
    </div>
  );
}
